import { Stack, useTheme } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import ReactApexcharts from 'src/@core/components/react-apexcharts';
import { useSettings } from 'src/@core/hooks/useSettings';
import router from 'next/router';
import { useMemo, useRef, useCallback } from 'react';
import useWindowSize from 'src/@core/hooks/useWindowResize';
import {
  useGetWidgetsJeopardyStatusQuery,
  useGetJeopardyStatusConfigurationsQuery,
  useGetJeopardyStatusCodesQuery
} from 'src/queries/services/salesorder';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import buildFilterString from '../../helpers/buildFilterString';

interface Bucket {
  label: string;
  colour: string;
  count: number;
  drillDownFilter: string;
}

const buildCodeFilter = (codes: { code?: string }[]): string => {
  const clauses = codes.filter(c => c.code).map(c => `jeopardyStatusCode eq '${c.code}'`);

  return clauses.length > 0 ? `(${clauses.join(' or ')})` : '';
};

const ConsignmentsOnTimeWidget = ({ dashboardDateStrings = [[''], ['']], orderByValue = '' }) => {
  const theme = useTheme();
  const { settings } = useSettings();
  const productLevel = useGetProductLevel();
  const { width } = useWindowSize();

  const isFlowUser = productLevel === Products.Flow || productLevel === Products.Quantum;

  const filterString = useMemo(
    () => buildFilterString(orderByValue, dashboardDateStrings),
    [orderByValue, dashboardDateStrings]
  );

  const { data: jeopardyMetrics } = useGetWidgetsJeopardyStatusQuery({
    params: { filter: filterString },
    enabled: isFlowUser
  });

  const metrics = jeopardyMetrics;
  const { data: configurations = [] } = useGetJeopardyStatusConfigurationsQuery({});
  const { data: statusCodes = [] } = useGetJeopardyStatusCodesQuery({
    params: { filter: 'isActive eq true' }
  });

  const configuration = useMemo(() => configurations.find(c => c.isEnabled) ?? configurations[0], [configurations]);

  const buckets = useMemo<Bucket[]>(
    () => [
      {
        label: configuration?.onTimeLabel ?? '',
        colour: configuration?.onTimeColour ?? '',
        count: metrics?.onTime ?? 0,
        drillDownFilter: buildCodeFilter(statusCodes.filter(c => c.isOnTime))
      },
      {
        label: configuration?.atRiskLabel ?? '',
        colour: configuration?.atRiskColour ?? '',
        count: metrics?.atRisk ?? 0,
        drillDownFilter: buildCodeFilter(statusCodes.filter(c => c.isAtRisk))
      },
      {
        label: configuration?.lateLabel ?? '',
        colour: configuration?.lateColour ?? '',
        count: metrics?.late ?? 0,
        drillDownFilter: buildCodeFilter(statusCodes.filter(c => c.isLate))
      }
    ],
    [metrics, configuration, statusCodes]
  );

  const bucketsRef = useRef(buckets);
  bucketsRef.current = buckets;

  const onClick = useCallback(
    (dataPointIndex: number) => {
      if (settings.unlockDashboardLayout) return;

      const bucket = bucketsRef.current[dataPointIndex];
      const base = `/uiflow/consignment?filter=${filterString}&extrafilter=isDeleted eq false`;

      router.push(bucket?.drillDownFilter ? `${base} and ${bucket.drillDownFilter}` : base);
    },
    [settings.unlockDashboardLayout, filterString]
  );

  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  const series = buckets.map(b => b.count);
  const labels = buckets.map(b => b.label);
  const colours = buckets.map(b => b.colour);
  const hasData = series.some(v => v > 0);

  const emptyColour = theme.palette.mode === 'light' ? '#e0e0e0' : '#3a3a3a';

  const { sm, lg } = theme.breakpoints.values;
  const centreLabelFontSize = width <= sm ? '12px' : '14px';
  const centreValueFontSize = width <= sm ? '32px' : width <= lg ? '42px' : '52px';

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'donut',
        events: {
          dataPointSelection: (_e, _ctx, config) => {
            if (hasData) onClickRef.current(config.dataPointIndex);
          }
        }
      },
      labels,
      colors: colours,
      fill: { colors: hasData ? colours : buckets.map(() => emptyColour) },
      legend: {
        show: true,
        floating: true,
        fontSize: '11px',
        fontWeight: 'bold',
        position: 'left',
        offsetX: width <= 1300 ? -10 : 50,
        offsetY: -5,
        labels: { useSeriesColors: true },
        formatter: (seriesName, opts) => `${seriesName}: ${buckets[opts.seriesIndex]?.count ?? 0}`,
        itemMargin: { vertical: 1 }
      },
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: centreLabelFontSize,
                fontWeight: '400',
                color: theme.palette.text.secondary,
                offsetY: -16
              },
              value: {
                show: true,
                fontSize: centreValueFontSize,
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                offsetY: 16,
                formatter: (val: string) => (hasData ? val : '0')
              },
              total: {
                show: true,
                label: buckets[0]?.label,
                fontSize: centreLabelFontSize,
                fontWeight: '400',
                color: theme.palette.text.secondary,
                formatter: () => String(buckets[0]?.count ?? 0)
              }
            }
          }
        }
      },
      stroke: { show: false },
      tooltip: { enabled: hasData, y: { formatter: (val: number) => `${val} orders` } },
      states: {
        hover: { filter: { type: hasData ? 'lighten' : 'none' } },
        active: { filter: { type: hasData ? 'darken' : 'none' } }
      },
      theme: { mode: theme.palette.mode }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme.palette.mode, theme.palette.text.primary, theme.palette.text.secondary, buckets, width, hasData]
  );

  if (!isFlowUser || !configuration) return null;

  return (
    <Stack height="100%" bgcolor="background.paper" borderRadius={1} sx={{ cursor: 'pointer' }}>
      <VigoTypography
        value="Consignments on Time"
        sx={{ textAlign: 'left', mt: 1.5, ml: 1.5, fontSize: '1.5rem', fontWeight: '700', flexShrink: 0 }}
      />
      <Stack flex={1} minHeight={0} overflow="hidden">
        <ReactApexcharts
          options={options}
          series={hasData ? series : buckets.map(() => 1)}
          type="donut"
          height="100%"
        />
      </Stack>
    </Stack>
  );
};

export default ConsignmentsOnTimeWidget;
